import React, { useCallback, useMemo } from 'react';
import { useResultsPageConfig } from '@de/providers/ResultsPageConfigProvider';
import { useLeadFlow } from '@de/providers/LeadFlowProvider';
import { useSubmittedProgramsOptional } from '@de/providers/SubmittedProgramsProvider';
import type { Result } from '@de/types/result';
import type { SubmittedProgram } from '../../ResultsPage/types';
import Submissions from '../../Submissions';

/**
 * `DslSubmissions` -- the governed Submissions recap surface for a
 * DSL-rendered result page.
 *
 * This is a platform-owned, config-gated runtime component (no new
 * context). It mirrors the imperative `ResultsPage` Submissions wiring
 * (`ResultsPage/variants/Default.tsx`) for the DSL render path:
 *
 *   - Renders the {@link Submissions} recap of programs the user has
 *     already submitted, sourcing them from the `SubmittedProgramsProvider`
 *     registry.
 *   - Gated on `resultsPage.submissions.enabled` via
 *     {@link useResultsPageConfig}. Operators may only toggle the section
 *     and set approved `title` / `subtitle` copy; the submission and
 *     next-steps business logic stays platform-owned.
 *   - Routes each per-program **Next Steps** CTA through the governed
 *     lead flow (`LeadFlowProvider.openLeadFlow`). Because the program is
 *     already submitted, the governed flow lands directly on the
 *     exit-strategy ("what to expect from the advisor") view rather than
 *     a fresh PII capture or the duplicate-school popup.
 *
 * Renders `null` when the section is disabled / unconfigured or when
 * there are no submitted programs, so non-result configs and
 * default-off experiences are unaffected.
 *
 * Hook dependencies (`useResultsPageConfig`, `useLeadFlow`,
 * `useSubmittedProgramsOptional`) are
 * satisfied by the provider stack `ConfigurableStep` mounts for result
 * configs; this component is rendered as a sibling of `<DslConfig>`
 * inside that stack.
 */
const DslSubmissions: React.FC = () => {
  const { shouldShowSubmissions, getSubmissionsCopy } = useResultsPageConfig();
  const { openLeadFlow, firstName } = useLeadFlow();
  const submitted = useSubmittedProgramsOptional();

  const submittedResultsFull = submitted?.submittedResultsFull;

  // Map the full submitted `Result[]` to the `SubmittedProgram[]` shape
  // the presentational component expects -- the same field mapping the
  // imperative `ResultsPage` uses in `trackSubmittedResult`.
  const submittedPrograms = useMemo<SubmittedProgram[]>(
    () =>
      (submittedResultsFull ?? []).map((result) => ({
        program: { id: result.programId, name: result.programName },
        school: { name: result.schoolName, slug: result.schoolSlug },
        applicationCtaLabel: result.applicationCtaLabel,
        applicationCtaUrl: result.applicationCtaURL,
      })),
    [submittedResultsFull],
  );

  // Reopen the governed flow for the already-submitted program behind a
  // Next Steps CTA. `openLeadFlow` detects the program as submitted and
  // routes straight to the exit-strategy view; the DSL never owns this
  // handoff.
  const handleViewNextSteps = useCallback(
    (submission: SubmittedProgram) => {
      const result = submittedResultsFull?.find(
        (r: Result) => r.programId === submission.program.id,
      );
      if (result) {
        openLeadFlow(result);
      }
    },
    [submittedResultsFull, openLeadFlow],
  );

  if (!shouldShowSubmissions || submittedPrograms.length === 0) {
    return null;
  }

  const copy = getSubmissionsCopy();

  return (
    <Submissions
      submissions={submittedPrograms}
      firstName={firstName}
      title={copy.title}
      subtitle={copy.subtitle}
      onViewNextSteps={handleViewNextSteps}
    />
  );
};

export default DslSubmissions;
